import React, { useEffect, useState, useCallback } from 'react'
import {
  Divider,
  Typography,
  Spin,
  Select,
  message,
  Empty,
  Collapse,
  Button,
  Form,
  Modal,
  Space,
  Tag,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons'
import { getMetaversesMainDiscovery } from 'src/api/metaverse'
import { getGamesByCategory } from 'src/api/game'
import { getSurvivalZone } from 'src/api/survivalzone'
import { getMazes } from 'src/api/maze'
import { getTreasureHunter } from 'src/api/treasurehunter'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import s from './index.module.scss'
import { LAYOUTTYPE, LAYOUTENTITYTYPE } from 'src/constants'
import { fetchCountryProvider } from 'src/api/authen'
import {
  getLayout,
  getLayoutSection,
  handleLayoutOrder,
  saveEditLayout,
  saveAddSectionItemLayout,
  deleteSectionItem,
  deleteSection,
} from 'src/api/layout'
import { manualFetch } from 'src/utils/fetch'
import Carousel from './carousel'
import CarouselItem from './carousel-item'
import SectionFormModal from './SectionFormModal'
const { Title } = Typography
const { Panel } = Collapse

const LayoutDiscovery: React.FC = () => {
  const [providerCountry, setProviderCountry] = useState('TN')
  const [layout, setLayout] = useState('GAMES')
  const [layoutId, setLayoutId] = useState('')
  const [currentSectionId, setCurrentSectionId] = useState('')
  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [previewSrc, setPreviewSrc] = useState<string>('')
  const [metaverseRooms, setMetaverseRooms] = useState<any[]>([])
  const [allGames, setAllGames] = useState<any[]>([])
  const [allGuys, setAllGuys] = useState<any[]>([])
  const [allMazes, setAllMazes] = useState<any[]>([])
  const [allTreasureHunter, setAllTreasureHunter] = useState<any[]>([])
  const [countries, setCountries] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [layouts, setLayouts] = useState<any[]>([])
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isAddSectionModalVisible, setIsAddSectionModalVisible] =
    useState(false)
  const [isAddItemSectionModalVisible, setIsAddItemSectionModalVisible] =
    useState(false)
  const [sectionItemsMap, setSectionItemsMap] = useState<Record<string, any[]>>(
    {}
  )

  const [editingSection, setEditingSection] = useState<any>(null)
  const [form] = Form.useForm()
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(
    null
  )

  const [entityOptions, setEntityOptions] = useState<any[]>([])
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null)
  const [imageCache, setImageCache] = useState<Record<string, string>>({})
  const [moveModalVisible, setMoveModalVisible] = useState(false)
  const [moveFromIndex, setMoveFromIndex] = useState<number | null>(null)
  const [moveToIndex, setMoveToIndex] = useState<number | null>(null)

  const fetchImage = async (url: string): Promise<string> => {
    if (!url) return ''
    if (imageCache[url]) {
      return imageCache[url]
    }
    try {
      const response = await manualFetch(url, {
        headers: { accept: 'image/*' },
      })
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      setImageCache((prev) => ({ ...prev, [url]: objectUrl }))
      return objectUrl
    } catch (error) {
      console.error('Failed to fetch image:', error)
      return ''
    }
  }

  const fetchMetaverse = async () => {
    const response = await getMetaversesMainDiscovery(0, 50)
    setMetaverseRooms(response.data || [])
  }

  const fetchGames = async () => {
    const response = await getGamesByCategory(0, 300)
    setAllGames(response.data || [])
  }

  const fetchGuys = async () => {
    const response = await getSurvivalZone(0, 300)
    setAllGuys(response.data || [])
  }

  const fetchTreasureHunter = async () => {
    const response = await getTreasureHunter(0, 300)
    setAllTreasureHunter(response.data || [])
  }

  const fetchMazes = async () => {
    const response = await getMazes(0, 300)
    setAllMazes(response.data || [])
  }

  const fetchCountries = async () => {
    try {
      const { data } = await fetchCountryProvider()
      setCountries(
        data.map((item: any) => ({
          label: item.countryName,
          value: item.countryCode,
        }))
      )
    } catch (err) {
      console.error(err)
      message.error('Failed to load countries')
    }
  }

  const fetchSection = useCallback(
    async (sectionId) => {
      try {
        const sectionRes = await getLayoutSection(sectionId)
        const games = sectionRes?.data || []
        const gamesWithImages = await Promise.all(
          games.map(async (game) => ({
            ...game,
            localThumb: await fetchImage(game.thumbnailS3Path),
          }))
        )
        setLayouts((prev) =>
          prev.map((s) =>
            s.sectionId === sectionId ? { ...s, games: gamesWithImages } : s
          )
        )

        setSectionItemsMap((prev) => ({
          ...prev,
          [sectionId]: games.map((g) => ({
            type: g.type,
            entityId: g.entityId || g.id,
          })),
        }))
      } catch (err) {
        console.error('Failed to fetch section:', err)
        message.error('Failed to reload section')
      }
    },
    [fetchImage]
  )

  const fetchLayoutData = useCallback(async () => {
    try {
      setLoading(true)
      const layoutRes = await getLayout(providerCountry, layout)
      setLayoutId(layoutRes.id)

      const layoutItems = layoutRes?.items || []
      const updatedLayouts: any[] = []
      for (const item of layoutItems) {
        if (!item.sectionId && item.type !== 'DYNAMIC') {
          updatedLayouts.push({ ...item, games: [] })
          continue
        }

        const sectionRes = await getLayoutSection(item.sectionId)
        const games = sectionRes?.data || []

        updatedLayouts.push({
          ...item,
          games,
        })

        setSectionItemsMap((prev) => ({
          ...prev,
          [item.sectionId]: games.map((g) => ({
            type: g.type,
            entityId: g.entityId || g.id,
          })),
        }))
      }

      setLayouts(updatedLayouts)

      const dynamicKeys = updatedLayouts
        .filter((sec) => sec.type === 'DYNAMIC')
        .map((sec) => sec.id)

      setOpenKeys(dynamicKeys)
      for (const item of updatedLayouts) {
        if (!item.games?.length) continue

        item.games.forEach(async (game, idx) => {
          try {
            const localThumb = await fetchImage(game.thumbnailS3Path)
            setLayouts((prev) =>
              prev.map((lay) =>
                lay.id === item.id
                  ? {
                      ...lay,
                      games: lay.games.map((g, i) =>
                        i === idx ? { ...g, localThumb } : g
                      ),
                    }
                  : lay
              )
            )
          } catch (err) {
            console.warn('Image load failed:', game.thumbnailS3Path)
          }
        })
      }
    } catch (err) {
      console.error(err)
      message.error('Failed to fetch layout data')
    } finally {
      setLoading(false)
    }
  }, [providerCountry, layout])

  const handleReorder = async (section, dragId, dropId) => {
    try {
      let updatedGames = []
      const reordered = [...section.games]
      const dragIndex = reordered.findIndex((g) => g.entityId === dragId)
      const dropIndex = reordered.findIndex((g) => g.entityId === dropId)
      if (dragIndex === -1 || dropIndex === -1) return section
      const [dragged] = reordered.splice(dragIndex, 1)
      reordered.splice(dropIndex, 0, dragged)
      updatedGames = reordered
      const updatedSectionItem = updatedGames.map((g) => ({
        type: g.type,
        entityId: g.entityId,
      }))
      await saveAddSectionItemLayout(
        section.sectionId,
        updatedSectionItem,
        true
      )
      message.success('Reordering Section Item Successfully')
      fetchSection(section.sectionId)
    } catch (err) {
      console.error('Failed to reorder items:', err)
    }
  }

  const performMove = async (prevIdx, newIdx) => {
    if (prevIdx == null || newIdx == null) return
    if (prevIdx === newIdx) {
      setMoveModalVisible(false)
      return
    }
    setLayouts((prev) => {
      const copy = [...prev]
      const [item] = copy.splice(prevIdx, 1)
      copy.splice(newIdx, 0, item)
      return copy
    })

    setMoveModalVisible(false)

    try {
      const res = await handleLayoutOrder(layoutId, prevIdx, newIdx)
      if (res === 'success') {
        message.success('Panel order updated')
        await fetchLayoutData()
      } else {
        throw new Error('API returned failed')
      }
    } catch (err) {
      console.error('Failed to save new order', err)
      message.error('Failed to save new order — reloading layout')
      await fetchLayoutData()
    } finally {
      setMoveFromIndex(null)
      setMoveToIndex(null)
    }
  }

  const openMoveModal = (sectionIndex) => {
    setMoveFromIndex(sectionIndex)
    setMoveToIndex(sectionIndex)
    setMoveModalVisible(true)
  }

  const confirmDeleteSection = (section) => {
    Modal.confirm({
      title: 'Delete Section?',
      content: `Are you sure you want to delete the section "${
        section.title || section.type
      }"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleDeleteSection(section),
    })
  }

  const handleDeleteSection = async (section) => {
    try {
      await deleteSection(layoutId, section.id)
      setLayouts((prev) => prev.filter((s) => s.id !== section.id))
      setSectionItemsMap((prev) => {
        const copy = { ...prev }
        delete copy[section.id]
        return copy
      })
      message.success('Section deleted successfully')
    } catch (err) {
      console.error('Failed to delete section:', err)
      message.error('Failed to delete section')
    }
  }

  const handleDelete = async (itemToDelete, section) => {
    try {
      const updatedSection = section.games
        .filter((g) => g.entityId !== itemToDelete.id)
        .map((g) => ({
          entityId: g.entityId,
          type: g.type,
        }))

      const index = section.games.findIndex(
        (g) => g.entityId === itemToDelete.id
      )
      await deleteSectionItem(section.sectionId, index)
      message.success('Deleted Section Item Successfully')
      fetchSection(section.sectionId)
      setSectionItemsMap((prev) => ({
        ...prev,
        [section.sectionId]: updatedSection,
      }))
    } catch (err) {
      console.error('Failed to delete section item:', err)
      message.error('Failed to delete section item')
    }
  }

  const handleCancelAddItemSection = () => {
    setIsAddItemSectionModalVisible(false)
    setSelectedEntityType(null)
    setEntityOptions([])
    setSelectedEntity(null)
    form.resetFields()
  }

  const handleAddItem = (section) => {
    const items = sectionItemsMap[section.sectionId] || []
    if (section.thumbnailSize === 'XLARGE' && items.length >= 1) {
      message.error(
        'This section allows only one item because the thumbnail size is XLARGE.'
      )
      return
    }
    setEditingSection(section)
    setCurrentSectionId(section.sectionId)
    setIsAddItemSectionModalVisible(true)
  }

  const handleEntityTypeChange = (value: string) => {
    setSelectedEntityType(value)
    setSelectedEntity(null)
    setEntityOptions([])

    try {
      let entities: any[] = []
      switch (value) {
        case 'GAME':
          entities = allGames
            .filter((s) => s.enabled === true)
            .map((g) => ({ label: g.name, value: g.id }))
          break
        case 'MAZE':
          entities = allMazes
            .filter((s) => s.enabled === true)
            .map((m) => ({ label: m.name, value: m.id }))
          break
        case 'GUYS':
          entities = allGuys
            .filter((s) => s.enabled === true)
            .map((z) => ({ label: z.name, value: z.id }))
          break
        case 'TREASURE':
          entities = allTreasureHunter
            .filter((s) => s.enabled === true)
            .map((t) => ({
              label: t.name,
              value: t.id,
            }))
          break
        case 'METAVERSE':
          entities = metaverseRooms.map((m) => ({
            label: m.name,
            value: m.roomId,
          }))
          break
        default:
          entities = []
      }
      setEntityOptions(entities)
    } catch (error) {
      console.error('Failed to fetch entities:', error)
      message.error('Failed to load items for selected type')
    }
  }

  const handleSaveAddItemSection = async () => {
    if (!selectedEntityType || !selectedEntity || !currentSectionId) return

    const section = layouts.find((s) => s.sectionId === currentSectionId)
    const items = sectionItemsMap[currentSectionId] || []
    if (section.thumbnailSize === 'XLARGE' && items.length >= 1) {
      message.error('Only one item is allowed in XLARGE thumbnail sections.')
      return
    }
    const newItem = {
      type: selectedEntityType,
      entityId: selectedEntity,
    }

    setSectionItemsMap((prev) => {
      const existing = prev[currentSectionId] || []
      const alreadyExists = existing.some(
        (item) => item.entityId === newItem.entityId
      )
      if (alreadyExists) return prev
      return {
        ...prev,
        [currentSectionId]: [...existing, newItem],
      }
    })

    try {
      const updatedItems = [...(items || [])]
      if (!updatedItems.some((item) => item.entityId === newItem.entityId)) {
        updatedItems.push(newItem)
      }

      await saveAddSectionItemLayout(currentSectionId, updatedItems, true)

      message.success('Added Section Item Successfully')

      setIsAddItemSectionModalVisible(false)
      setSelectedEntityType(null)
      setSelectedEntity(null)
      fetchSection(currentSectionId)
    } catch (err) {
      console.error('Failed to save section items:', err)
      message.error('Failed to save section items')
    }
  }

  const handleEditSection = async (section: any, e: React.MouseEvent) => {
    setCurrentSectionId(section.sectionId)
    e.stopPropagation()
    setEditingSection(section)
    setIsEditModalVisible(true)
    form.setFieldsValue({
      name: section.name,
      title: section.title,
      description: section.description,
      iconS3Path: section.iconS3Path,
      thumbnailSize: section.thumbnailSize,
      type: section.type,
      enable: section.enable !== false,
      viewAll: section.viewAll !== false,
      sectionId: section.sectionId,
      autoShuffle:
        section.autoShuffle !== null && section.autoShuffle !== false,
    })
    setPreviewSrc('')
    setUploadedImage(null)
    if (section.iconS3Path) {
      const objUrl = await fetchImage(section.iconS3Path)
      setPreviewSrc(objUrl)
    }
  }

  const handleImageUpload = (info: any) => {
    const file = info.file.originFileObj || info.file
    if (file) {
      const localPreview = URL.createObjectURL(file)
      setUploadedImage(file)
      setPreviewSrc(localPreview)
      form.setFieldsValue({ iconS3Path: '' })
    }
  }

  const handleSectionSubmit = async (values: any) => {
    try {
      if (values.thumbnailSize === 'XLARGE' && values.sectionId) {
        const items = sectionItemsMap[values.sectionId] || []
        if (items.length > 1) {
          const trimmed = [items[0]]
          try {
            await saveAddSectionItemLayout(values.sectionId, trimmed, true)

            setSectionItemsMap((prev) => ({
              ...prev,
              [values.sectionId]: trimmed,
            }))
          } catch (err) {
            console.error('Failed to trim items for XLARGE change', err)
            message.warning('Failed to trim extra items automatically.')
          }
        }
      }

      if (editingSection && isEditModalVisible) {
        await saveEditLayout(layoutId, editingSection.id, values, uploadedImage)
      } else {
        await saveEditLayout(layoutId, null, values, uploadedImage)
      }

      setIsEditModalVisible(false)
      setIsAddSectionModalVisible(false)
      setUploadedImage(null)
      setPreviewSrc('')
      fetchLayoutData()
    } catch (err) {
      console.error(err)
      message.error('Failed to save section')
    }
  }

  const clearImage = () => {
    form.setFieldsValue({ iconS3Path: '' })
    setUploadedImage(null)
    setPreviewSrc('')
  }

  useEffect(() => {
    fetchCountries()
    fetchLayoutData()
  }, [fetchLayoutData])

  useEffect(() => {
    fetchMetaverse()
    fetchGames()
    fetchGuys()
    fetchMazes()
    fetchTreasureHunter()
  }, [])

  return (
    <Spin spinning={loading}>
      <div className={s.layoutDiscovery}>
        <Title level={3}>Layout</Title>
        <Divider />
        <div className={s.layoutControls}>
          <div>
            <Select
              placeholder="Select Country"
              value={providerCountry}
              onChange={setProviderCountry}
              options={countries}
              style={{ width: 180 }}
            />
            <Select
              placeholder="Select Layout"
              value={layout}
              onChange={setLayout}
              options={LAYOUTTYPE}
              style={{ width: 160, marginLeft: 12 }}
            />
          </div>
          <Button
            onClick={() => {
              form.resetFields()
              setUploadedImage(null)
              setPreviewSrc('')
              setEditingSection(null)
              setIsAddSectionModalVisible(true)
            }}
            type="primary"
            className="btn-primary"
            size="middle"
            style={{ width: 140 }}
          >
            + Add Section
          </Button>
        </div>
        <Divider />
        <DndProvider backend={HTML5Backend}>
          <div style={{ textAlign: 'right', marginBottom: 16 }}></div>

          {layouts.length > 0 ? (
            <Collapse
              activeKey={openKeys}
              onChange={(keys) => setOpenKeys(keys as string[])}
              className={s.layoutCollapse}
              ghost
            >
              {layouts.map((section) => (
                <Panel
                  key={section.id}
                  header={
                    <div className={s.panelHeader}>
                      <Title level={4} className={s.sectionTitle}>
                        {section.title}
                        {section.games?.length > 0 && (
                          <span className={s.gameCount}>
                            ({section.games.length})
                          </span>
                        )}
                      </Title>
                      <div
                        className={s.headerActions}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          type="text"
                          style={{ color: '#1790ae' }}
                          icon={<EditOutlined />}
                          onClick={(e) => handleEditSection(section, e)}
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          position: 'absolute',
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <Button
                          type="text"
                          onClick={() => confirmDeleteSection(section)}
                          style={{ color: 'red' }}
                          title="Delete section"
                          icon={<DeleteOutlined />}
                        />

                        <Button
                          type="text"
                          onClick={(e) => {
                            e.stopPropagation()
                            const idx = layouts.findIndex(
                              (l) => l.id === section.id
                            )
                            openMoveModal(
                              idx >= 0 ? idx : layouts.indexOf(section)
                            )
                          }}
                          style={{ color: '#1790ae' }}
                          title="Move section"
                          icon={<ColumnHeightOutlined />}
                        />
                        <Tag
                          color={section.enable ? 'green' : 'red'}
                          style={{
                            margin: 0,
                            fontWeight: 500,
                          }}
                        >
                          {section.enable ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </div>
                    </div>
                  }
                  showArrow={true}
                  className={s.collapsePanel}
                >
                  {section.type === 'DYNAMIC' ? (
                    <div className={s.carouselWrapper}>
                      <Carousel
                        name={section.title}
                        sectionItem={section}
                        onAddClick={(item) => handleAddItem(item)}
                      >
                        {section.games?.length > 0 ? (
                          section.games.map((game: any) => (
                            <CarouselItem
                              key={game.entityId}
                              layoutCard={{
                                id: game.entityId,
                                title: game.title,
                                cover_url: game.localThumb,
                                number_tracks: game.totalTracks || 0,
                              }}
                              categoryId={section.id}
                              onDrop={(dragId, dropId) => {
                                handleReorder(section, dragId, dropId)
                              }}
                              onRemove={(item) => handleDelete(item, section)}
                            />
                          ))
                        ) : (
                          <div
                            style={{
                              padding: '60px 20px',
                              textAlign: 'center',
                            }}
                          ></div>
                        )}
                      </Carousel>
                    </div>
                  ) : (
                    <span style={{ color: 'white', font: 'caption' }}>
                      Static Data
                    </span>
                  )}
                </Panel>
              ))}
            </Collapse>
          ) : (
            <Empty
              description="No layout data available"
              className={s.noData}
            />
          )}
        </DndProvider>

        <SectionFormModal
          visible={isEditModalVisible || isAddSectionModalVisible}
          mode={isEditModalVisible ? 'edit' : 'add'}
          form={form}
          uploadedImage={uploadedImage}
          previewSrc={previewSrc}
          onCancel={() => {
            form.resetFields()
            setUploadedImage(null)
            setPreviewSrc('')
            setIsEditModalVisible(false)
            setIsAddSectionModalVisible(false)
            setEditingSection(null)
          }}
          onUpload={handleImageUpload}
          onClearImage={clearImage}
          onSubmit={handleSectionSubmit}
          existingSections={layouts}
          isEditingExisting={isEditModalVisible}
        />

        <Modal
          title={'Add Section Item'}
          visible={isAddItemSectionModalVisible}
          onCancel={handleCancelAddItemSection}
          footer={null}
          width={700}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="Select Entity Type"
              options={LAYOUTENTITYTYPE}
              onChange={handleEntityTypeChange}
              value={selectedEntityType}
              style={{ width: '40%' }}
            />
            {selectedEntityType && (
              <Select
                placeholder={`Select ${selectedEntityType}`}
                options={entityOptions}
                value={selectedEntity}
                onChange={setSelectedEntity}
                style={{ width: '40%' }}
                showSearch
                optionFilterProp="label"
              />
            )}
          </Space>
          <div className={s.modalActions}>
            <Button
              type="primary"
              htmlType="button"
              className={s.saveButton}
              onClick={handleSaveAddItemSection}
              disabled={!selectedEntityType || !selectedEntity}
            >
              Save Changes
            </Button>
            <Button
              onClick={handleCancelAddItemSection}
              className={s.cancelButton}
            >
              Cancel
            </Button>
          </div>
        </Modal>

        <Modal
          title="Move Section"
          visible={moveModalVisible}
          onCancel={() => {
            setMoveModalVisible(false)
            setMoveFromIndex(null)
            setMoveToIndex(null)
          }}
          onOk={() => {
            if (moveFromIndex !== null && moveToIndex !== null) {
              performMove(moveFromIndex, moveToIndex)
            } else {
              setMoveModalVisible(false)
            }
          }}
          okText="Move"
          cancelText="Cancel"
        >
          <div style={{ marginBottom: 12 }}>
            <strong>Move section:</strong>{' '}
            {moveFromIndex != null
              ? layouts[moveFromIndex]?.title ?? `#${moveFromIndex + 1}`
              : ''}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Choose target position
            </label>
            <Select
              value={moveToIndex !== null ? moveToIndex : undefined}
              onChange={(val) => setMoveToIndex(val)}
              style={{ width: '100%' }}
              options={layouts.map((l, idx) => ({
                label: `${idx + 1} — ${l.title || `Section ${idx + 1}`}`,
                value: idx,
              }))}
              placeholder="Select position"
            />
          </div>
        </Modal>
      </div>
    </Spin>
  )
}
LayoutDiscovery.displayName = 'LayoutDiscovery'
export default LayoutDiscovery
